import { useEffect, useState } from "react";
import useSelectedAccount from "../../../hooks/useSelectedAccount";
import { AllDestinations } from "../../../models";
import { useGetDestinationsQuery } from "../../../redux/services/destinations";

interface UseDestinationsOptions {
  limit?: number;
  initialSearch?: string;
}

interface UseDestinationsReturn {
  // Data
  destinations: AllDestinations[];
  totalCount: number;
  searchTotalCount: number;

  // Search
  searchInput: string;
  setSearchInput: (search: string) => void;
  searchQuery: string;
  setSearchQuery: (search: string) => void;

  // Pagination
  offset: number;
  setOffset: (offset: number) => void;
  shouldShowPagination: boolean;
  limit: number;

  // Loading states
  isInitialLoading: boolean;
  isRefetching: boolean;
  isInitialized: boolean;
  error: any;

  // Actions
  refetch: () => void;
}

export const useDestinations = ({
  limit = 20,
  initialSearch = "",
}: UseDestinationsOptions = {}): UseDestinationsReturn => {
  const { accountId } = useSelectedAccount();

  // Search state
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  // Pagination and totals
  const [totalCount, setTotalCount] = useState(0);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);

  // API query
  const {
    data,
    error,
    refetch,
    isUninitialized,
    isLoading: isInitialLoading,
    isFetching: isRefetching,
  } = useGetDestinationsQuery(
    {
      accountId,
      offset,
      limit,
      search: searchQuery ?? "",
      includeTotalCount: true,
    },
    { skip: !accountId }
  );

  // Reset states when account changes
  useEffect(() => {
    setTotalCount(0);
    setSearchTotalCount(0);
    setOffset(0);
    setSearchInput("");
    setSearchQuery("");
  }, [accountId]);

  // Handle initialSearch changes
  useEffect(() => {
    setSearchInput(initialSearch);
    setSearchQuery(initialSearch);
  }, [initialSearch]);

  // Refetch when account or offset changes
  useEffect(() => {
    if (accountId) {
      refetch();
    }
  }, [accountId, offset, refetch]);

  // Update total count
  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setTotalCount(data.totalCount);
    }
  }, [data?.totalCount]);

  // Update search total when search is active
  useEffect(() => {
    if (searchQuery && data?.totalCount !== undefined) {
      setSearchTotalCount(data.totalCount);
    }
  }, [data?.totalCount, searchQuery]);

  // Reset offset when search query changes
  useEffect(() => {
    setOffset(0);
  }, [searchQuery]);

  // Derived values
  const destinations = data?.data ?? [];
  const effectiveTotalCount = searchQuery ? searchTotalCount : totalCount;
  const shouldShowPagination = effectiveTotalCount > limit;

  return {
    // Data
    destinations,
    totalCount: effectiveTotalCount,
    searchTotalCount,

    // Search
    searchInput,
    setSearchInput,
    searchQuery,
    setSearchQuery,

    // Pagination
    offset,
    setOffset,
    shouldShowPagination,
    limit,

    // Loading states
    isInitialLoading,
    isRefetching,
    isInitialized: !isUninitialized,
    error,

    // Actions
    refetch,
  };
};

export default useDestinations;
